import { ColorNordicGrey1, ColorOpacityDark2 } from 'autogen/design-tokens/tokens';
import type { ProductReviewModel } from 'autogen/swagger/all';
import { IsPrelaunch } from 'autogen/translation-keys/trans-website-productreview-list';
import Avatar from 'components/generic/Avatar';
import { Typography } from 'components/generic/typography';
import React, { type HtmlHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { format } from 'modules/helpers/strings';
import { Age } from 'autogen/translation-keys/trans-website-shared';
import type { IAvatar } from 'components/generic/Avatar/shared/types';
import { breakpoint } from 'variables';
import InlineButton from 'components/generic/InlineButton';
import dynamic from 'helpers/dynamic';
import { VerifiedPurchaseTag } from 'autogen/translation-keys/trans-website-productreview';

const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
`;

const UserContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const StyledAvatar = styled(Avatar)`
    grid-row: span 2;
`;

const ReviewInformation = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    ${breakpoint.up(370)} {
        align-items: flex-end;
    }
`;

const ReviewAge = styled(Typography.Body)`
    color: ${ColorOpacityDark2};
`;

const Verification = styled(Typography.Body)`
    color: ${ColorOpacityDark2};
`;

interface IUserInformation extends HtmlHTMLAttributes<HTMLDivElement> {
    model: ProductReviewModel;
}

export default function UserInformation({ model, ...restProps }: IUserInformation): JSX.Element {
    const { firstName, age, timeAgoText, isPrelaunch, isVerifiedPurchase, prelaunchInfoLink, isAnonymized } = model;
    const { t } = useTranslation();

    const AvatarParams: IAvatar = {
        avatar: isAnonymized ? '?' : firstName,
        backgroundColor: ColorNordicGrey1
    };

    const prelaunchReadMoreClick = async () => {
        const { default: htmlModal } = await import(/* webpackChunkName: "htmlModal" */ 'modules/modal/htmlModal');
        const modal = htmlModal();

        modal({ open: true, url: prelaunchInfoLink.link.linkAction.url });
    };

    return (
        <Wrapper {...restProps}>
            <UserContainer>
                <StyledAvatar {...AvatarParams} />
                <Typography.Body as="span" size="M" strong>
                    {firstName}
                    {age && `, ${format(t(Age))(age)}`}
                </Typography.Body>
            </UserContainer>
            <ReviewInformation>
                <ReviewAge as="span">{timeAgoText}</ReviewAge>
                <Verification as="span">
                    {isPrelaunch && (
                        <InlineButton variant="inherit" onClick={prelaunchReadMoreClick}>
                            {t(IsPrelaunch)}
                        </InlineButton>
                    )}
                    {isVerifiedPurchase && !isPrelaunch && t(VerifiedPurchaseTag)}
                </Verification>
            </ReviewInformation>
        </Wrapper>
    );
}
